import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import { setAuthSession } from '../store/auth.slice'
import { deleteForm, fetchForms, publishForm, setPayload, setThumbnails } from '../store/form.slice'
import { getObject } from '../services/aws.service'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { useUrlAuth } from '../utils/useUrlAuth'
import {
  capitalize,
  getDirtySubmissionForm,
  isFormDirty,
  isMobile,
  parseDate,
  searchAndFilterForms
} from '../utils/functions'
import { UserSchema } from '../utils/types/auth'
import { FormSchema, NewFormPayloadSchema } from '../utils/types/form'
import SVG from '../assets/undraw_the-void_i26b.svg'

export const DashboardView = () => {
  const [view, setView] = useState<'grid' | 'list'>()
  const [forms, setForms] = useState<FormSchema[]>([])
  const [selectedForm, setSelectedForm] = useState<FormSchema | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [filterBy, setFilterBy] = useState('')
  const [isFilterOptionsOpen, setIsFilterOptionsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState({
    authSession: false,
    forms: false
  })
  const [requestState, setRequestState] = useState({
    info: '',
    success: '',
    error: ''
  })
  const searchRef = useRef<HTMLInputElement | null>(null)
  const formsListRef = useRef<(HTMLLIElement | null)[]>([])
  const filterByTriggerRef = useRef(null)
  const filterByContextRef = useRef(null)
  const selectedFormTriggerRef = useRef(null)
  const selectedFormContextRef = useRef(null)

  useOutsideClick(
    {
      element: filterByContextRef,
      trigger: filterByTriggerRef
    },
    () => setIsFilterOptionsOpen(false)
  )

  useOutsideClick({ element: selectedFormContextRef, trigger: selectedFormTriggerRef }, () =>
    setSelectedForm(null)
  )

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, thumbnails } = formState
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const { urlAuth } = useUrlAuth()

  const isSignedUp = urlAuth.get('type') === 'signup'

  useEffect(() => {
    if (isSignedUp) {
      initNewUser()
    }

    const draft = localStorage.getItem('draft')

    if (draft) {
      const draftPayload: NewFormPayloadSchema = JSON.parse(draft)
      dispatch(setPayload({ form: draftPayload.form }))
      navigate('/form/new/preview')

      localStorage.removeItem('draft')
    }

    const viewPref = localStorage.getItem('view') as 'grid' | 'list' | null
    setView(viewPref ? viewPref : isMobile() ? 'list' : 'grid')

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (authUser) {
      initDashboard(authUser)
    }
    // eslint-disable-next-line
  }, [authUser])

  useEffect(() => {
    handleFilter()
    populateThumbnails(globalForms)
    // eslint-disable-next-line
  }, [globalForms])

  const initNewUser = async () => {
    const clientAuth = {
      access_token: urlAuth.get('access_token'),
      refresh_token: urlAuth.get('refresh_token'),
      expires_at: urlAuth.get('expires_at'),
      expires_in: urlAuth.get('expires_in')
    }

    try {
      setIsLoading({ ...isLoading, authSession: true })
      await dispatch(setAuthSession(clientAuth)).unwrap()
      navigate('/dashboard', { replace: true, viewTransition: true })
    } catch (error: any) {
      setRequestState({ ...requestState, error: error.message })
    } finally {
      setIsLoading({ ...isLoading, authSession: false })
    }
  }

  const initDashboard = async (authUser: UserSchema) => {
    if (!authUser.forms.length || authUser.forms.length === globalForms.length) {
      // Do nothing
    } else {
      try {
        setIsLoading({ ...isLoading, forms: true })
        await dispatch(fetchForms()).unwrap()
      } catch (error: any) {
        setRequestState({ ...requestState, error: error.message })
      } finally {
        setIsLoading({ ...isLoading, forms: false })
      }
    }
  }

  const populateThumbnails = async (forms: FormSchema[]) => {
    const unresolvedFormIds: string[] = []

    forms.forEach(({ _id }) => {
      if (!Object.keys(thumbnails).includes(_id)) {
        unresolvedFormIds.push(_id)
      }
    })

    if (unresolvedFormIds.length) {
      const allRequests = []

      try {
        const newThumbnails: Record<string, string> = {}

        for (const formId of unresolvedFormIds) {
          allRequests.push(getObject(formId))
        }

        const allUrls = await Promise.all(allRequests)

        for (let i = 0; i < unresolvedFormIds.length; i++) {
          for (let j = 0; j < allUrls.length; j++) {
            const url = allUrls[j]

            if (url && i === j) {
              newThumbnails[unresolvedFormIds[i]] = url
            }
          }
        }

        dispatch(setThumbnails(newThumbnails))
      } catch (error: any) {
        setRequestState({ ...requestState, error: error.message })
      }
    }
  }

  const handleViewList = (view: 'grid' | 'list') => {
    setView(view)
    localStorage.setItem('view', view)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value
    setSearchValue(searchValue)
    handleFilter(filterBy, searchValue)
  }

  const handleFilter = (filterBy: string = '', searchBy: string = searchValue) => {
    const filteredForms = searchAndFilterForms({
      items: globalForms,
      searchBy,
      filterBy
    })

    setForms(filteredForms as FormSchema[])
    setFilterBy(filterBy)
  }

  const handleSelectForm = async (e: React.MouseEvent<HTMLSpanElement>, form: FormSchema) => {
    e.stopPropagation()

    selectedForm
      ? selectedForm._id === form._id
        ? setSelectedForm(null)
        : setSelectedForm(form)
      : setSelectedForm(form)
  }

  const handlePublishForm = (e: React.MouseEvent<HTMLSpanElement>, form: FormSchema) => {
    e.stopPropagation()

    if (form.publishedOn && !window.confirm(`Confirm unpublish: ${form.name}`)) {
      return
    }

    dispatch(publishForm({ id: form._id, isPublished: !form.publishedOn }))
  }

  const handleEditForm = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()

    if (selectedForm) {
      if (selectedForm.submissions.length) {
        setRequestState({
          ...requestState,
          info: `Cannot edit form "${selectedForm.name}" if ${selectedForm.isPublic ? 'responses' : 'entries'} are already submitted`
        })
        setSelectedForm(null)
      } else if (isFormDirty()) {
        setRequestState({
          ...requestState,
          info: 'Cannot edit form while another form is being drafted'
        })
        setSelectedForm(null)
      } else {
        dispatch(setPayload({ form: selectedForm }))
        navigate(`/form/${selectedForm._id}/edit`, { viewTransition: true })
      }
    }
  }

  const handleDeleteForm = async (
    e: React.MouseEvent<HTMLSpanElement>,
    form: FormSchema,
    index: number
  ) => {
    e.stopPropagation()

    if (window.confirm(`Confirm delete: ${form.name}`)) {
      requestState.error && setRequestState({ ...requestState, error: '' })

      if (selectedForm) {
        try {
          formsListRef.current[index]?.classList.add('disabled', 'disabled-no-fade')
          const formId = selectedForm._id
          setSelectedForm(null)

          await dispatch(deleteForm(formId)).unwrap()
        } catch (error: any) {
          setRequestState({ ...requestState, error: error.message })
          formsListRef.current[index]?.classList.remove('disabled', 'disabled-no-fade')
        }
      }
    }
  }

  const handleAddSubmission = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    form: FormSchema,
    dirtySubmissionForm: FormSchema | null,
    isSubmittingForm: boolean
  ) => {
    e.stopPropagation()

    if (dirtySubmissionForm && !isSubmittingForm) {
      setRequestState({
        ...requestState,
        info: 'Submission already in progress!'
      })
      return
    }

    navigate(`/form/${form._id}/submit`, { viewTransition: true })
  }

  return (
    <>
      {isLoading.authSession ? (
        <Spinner size='center' />
      ) : (
        <>
          {requestState.info && (
            <Status
              info={requestState.info}
              handler={{
                label: 'Continue with draft',
                callback: () => navigate(`/form/new/edit`, { viewTransition: true })
              }}
              reset={() => setRequestState({ ...requestState, info: '' })}
            />
          )}
          {requestState.error && (
            <Status
              error={requestState.error}
              reset={() => setRequestState({ ...requestState, error: '' })}
            />
          )}
          <div className='main_content dashboard_content'>
            <h1 className='heading-styled'>Home</h1>
            <div
              className='dashboard-new'
              onClick={() =>
                navigate(`/form/new/${isFormDirty() ? 'edit' : 'select'}`, {
                  viewTransition: true
                })
              }>
              {isFormDirty() ? (
                <>
                  <span className='material-symbols-rounded'>arrow_right_alt</span>
                  <span>Continue with draft...</span>
                </>
              ) : (
                <>
                  <span className='material-symbols-rounded'>add</span>
                  <span>New form</span>
                </>
              )}
            </div>
            <button
              className='button-with-icon cta'
              onClick={() => navigate(`/templates`, { viewTransition: true })}>
              <span>Explore templates</span>
              <span className='material-symbols-rounded'>arrow_right_alt</span>
            </button>
            <section>
              <div className='title_container'>
                <h2>My forms</h2>
                <p>Forms in my collection</p>
              </div>
              <div className='input_container-subtext'>
                <div className='input_container input_container-reverse'>
                  <span
                    className='material-symbols-rounded'
                    onClick={() => searchRef.current?.focus()}>
                    search
                  </span>
                  <input
                    type='search'
                    value={searchValue}
                    onChange={handleSearch}
                    placeholder='Search forms, sections, questions...'
                    ref={searchRef}
                  />
                </div>
                {!!forms.length && (
                  <span className='subtext subtext-thin'>
                    {`${searchValue || filterBy ? `${forms.length} match${forms.length === 1 ? '' : 'es'} found` : 'Showing all forms'}`}
                  </span>
                )}
              </div>
              <div className='view_as'>
                <span
                  className={`material-symbols-rounded material-symbols-sm ${view === 'grid' ? 'material-symbols-selected' : ''}`}
                  onClick={() => handleViewList('grid')}>
                  grid_view
                </span>
                <span
                  className={`material-symbols-rounded material-symbols-sm ${view === 'list' ? 'material-symbols-selected' : ''}`}
                  onClick={() => handleViewList('list')}>
                  table_rows
                </span>
                <div className='actions_container'>
                  <div className='search_container'>
                    <div className='actions-select'>
                      <div className='actions_series'>
                        <div>
                          <div
                            id='filters_trigger'
                            className='div-with-icon'
                            onClick={() => setIsFilterOptionsOpen(!isFilterOptionsOpen)}
                            ref={filterByTriggerRef}>
                            <span id='filters_trigger'>
                              Showing: {capitalize(filterBy) || 'All'}
                            </span>
                            <span id='filters_trigger' className='material-symbols-rounded'>
                              {isFilterOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                            </span>
                          </div>
                          {isFilterOptionsOpen && (
                            <div
                              className='context_menu context_menu-down context_menu-wrap'
                              id='filtersContext'
                              ref={filterByContextRef}>
                              <div className='context_menu-grid context-header'>
                                <input
                                  id='filter_all'
                                  type='checkbox'
                                  checked={!filterBy}
                                  onChange={() => handleFilter()}
                                />
                                <label htmlFor='filter_all' className='highlight-styled-hover'>
                                  All
                                </label>
                                <input
                                  id='filter_private'
                                  type='checkbox'
                                  checked={filterBy === 'private'}
                                  onChange={() => handleFilter('private')}
                                />
                                <label htmlFor='filter_private' className='highlight-styled-hover'>
                                  Private
                                </label>
                                <input
                                  id='filter_public'
                                  type='checkbox'
                                  checked={filterBy === 'public'}
                                  onChange={() => handleFilter('public')}
                                />
                                <label htmlFor='filter_public' className='highlight-styled-hover'>
                                  Public
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isLoading.forms ? (
                <span>...</span>
              ) : (
                <div>
                  {forms.length ? (
                    <ul className={`content ${view === 'grid' ? 'content-grid' : 'content-list'}`}>
                      {forms.map((form, index) => {
                        const dirtySubmissionForm = getDirtySubmissionForm()
                        const isSubmittingForm = dirtySubmissionForm?._id === form._id

                        return (
                          <li
                            className={`theme-content-${form.meta.colour}`}
                            onClick={() =>
                              navigate(`/form/${form._id}?graph=calendar`, {
                                viewTransition: true
                              })
                            }
                            key={form._id}
                            ref={(el) => {
                              formsListRef.current[index] = el
                            }}>
                            <div className='card_container'>
                              {!thumbnails[form._id] ? (
                                <Spinner />
                              ) : (
                                <>
                                  <img src={thumbnails[form._id]} alt={form.name} />
                                  {view === 'grid' && (
                                    <>
                                      {form.isPublic ? (
                                        <div className='img-actions'>
                                          <span
                                            className={
                                              'badge ' +
                                              (form.publishedOn ? 'badge-green' : 'badge-grey')
                                            }>
                                            {form.publishedOn ? 'Live' : 'Offline'}
                                          </span>
                                        </div>
                                      ) : (
                                        <div
                                          className={`img-actions theme-content-${form.meta.colour}`}
                                          onClick={(e) =>
                                            handleAddSubmission(
                                              e,
                                              form,
                                              dirtySubmissionForm,
                                              isSubmittingForm
                                            )
                                          }>
                                          <span className='material-symbols-rounded badge-rounded'>
                                            {isSubmittingForm ? 'arrow_right_alt' : 'add'}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                            <div className='content-item-title'>
                              <div>
                                <h3>{form.name}</h3>
                                {view === 'list' && (
                                  <>
                                    <span>
                                      <em
                                        className={
                                          form.isPublic ? 'badge-green-icon' : 'badge-blue-icon'
                                        }>
                                        {' ● '}
                                      </em>
                                      <em
                                        className={
                                          'badge ' + (form.isPublic ? 'badge-green' : 'badge-blue')
                                        }>
                                        {form.isPublic ? 'Public' : 'Private'}
                                      </em>
                                    </span>
                                    {form.isPublic && (
                                      <span>
                                        <em
                                          className={
                                            form.publishedOn
                                              ? 'badge-green-icon'
                                              : 'badge-grey-icon'
                                          }>
                                          {' ● '}
                                        </em>
                                        <em
                                          className={
                                            'badge ' +
                                            (form.publishedOn ? 'badge-green' : 'badge-grey')
                                          }>
                                          {form.publishedOn ? 'Live' : 'Offline'}
                                        </em>
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              {view === 'grid' && (
                                <span>
                                  <em
                                    className={
                                      form.isPublic ? 'badge-green-icon' : 'badge-blue-icon'
                                    }>
                                    {' ● '}
                                  </em>
                                  <em
                                    className={
                                      'badge ' + (form.isPublic ? 'badge-green' : 'badge-blue')
                                    }>
                                    {form.isPublic ? 'Public' : 'Private'}
                                  </em>
                                </span>
                              )}
                              {view === 'list' && (
                                <div className='content-item-details content-item-details-grid'>
                                  <span>{form.description || 'Description: --'}</span>
                                  <span>
                                    {`${form.isPublic ? 'Responses' : 'Entries'}: ${form.submissions.length || '--'}`}
                                  </span>
                                </div>
                              )}
                            </div>
                            {view === 'grid' && (
                              <div className='content-item-details'>
                                <span>Created: {parseDate(form.createdOn, 'date')}</span>
                                <span>
                                  {`${form.isPublic ? 'Responses' : 'Entries'}: ${form.submissions.length || '--'}`}
                                </span>
                              </div>
                            )}
                            <div className='options'>
                              <span
                                id='options_trigger'
                                className='material-symbols-rounded options_trigger'
                                onClick={(e) => handleSelectForm(e, form)}
                                ref={selectedFormTriggerRef}>
                                more_vert
                              </span>
                              {selectedForm?._id === form._id && (
                                <div
                                  className={`context_menu ${view === 'list' ? 'context_menu-right' : ''} context_menu-wrap`}
                                  ref={selectedFormContextRef}>
                                  {form.isPublic && (
                                    <div
                                      className='div-with-icon highlight-styled-hover'
                                      onClick={(e) => handlePublishForm(e, form)}>
                                      <span
                                        className={`material-symbols-rounded ${form.publishedOn ? 'on' : ''}`}>
                                        {form.publishedOn ? 'toggle_on' : 'toggle_off'}
                                      </span>
                                      <span>{form.publishedOn ? 'Published' : 'Publish'}</span>
                                    </div>
                                  )}
                                  {!form.isPublic && (
                                    <div
                                      className='div-with-icon highlight-styled-hover'
                                      onClick={(e) =>
                                        handleAddSubmission(
                                          e,
                                          form,
                                          dirtySubmissionForm,
                                          isSubmittingForm
                                        )
                                      }>
                                      <span className='material-symbols-rounded'>add</span>
                                      <span>Add entry</span>
                                    </div>
                                  )}
                                  <div
                                    className='div-with-icon highlight-styled-hover'
                                    onClick={handleEditForm}>
                                    <span className='material-symbols-rounded'>edit</span>
                                    <span>Edit form</span>
                                  </div>
                                  <div
                                    className='div-with-icon error'
                                    onClick={(e) => handleDeleteForm(e, form, index)}>
                                    <span className='material-symbols-rounded'>delete</span>
                                    <span>Delete form</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div className='svg_container'>
                      <img src={SVG} alt='No results' />
                      <span className='no_results'>No results.</span>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </>
  )
}
